// Appearance is a sheet over the screen being looked at (B-399,
// PLAN-2026-10-17 §2.2, DIRECTION U24).
//
// It was a page at `/account/appearance`, outside the league, that nothing on
// it returned from: a one-way trip, and the look it changed was seen on the
// wrong screen. Now the account menu opens it over whatever is on screen, a
// change in it applies there at once, and closing it — its ✕, Back, or a pull
// down on a phone — leaves that screen exactly as it was.
//
// THE ADDRESS STILL OPENS IT. A link to `/account/appearance` (an
// achievement's "Open Appearance", the tour's chapter, a bookmark) goes back
// to the screen it was followed from and opens the sheet over it; arrived at
// from outside the app, it opens over Account (a visitor's, the chapters).
// The address itself never stays in the bar.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet } from '../kit/overlays';
import { AppearanceBody } from '../screens/AppearanceScreen';
import { useSession } from '../data/session';
import { useSim } from '../data/SimProvider';
import { stepsToScreenBefore } from './wayBack';

/** The sheet's address, kept so every link that names it still works. */
export const APPEARANCE_PATH = '/account/appearance';

const OPEN = 'fsp:appearance';

/** The address asked for the sheet, over the screen it is going back to. */
let pending = false;

/** Open the sheet over the screen on view: the account menu, a visitor's. */
export function openAppearance(): void {
  window.dispatchEvent(new Event(OPEN));
}

/** The sheet, mounted once in the frame. */
export function AppearanceSheet() {
  // The frame is drawn afresh when the address went back to the hub. Read,
  // not taken: development draws this twice and keeps the second.
  const [opened, setOpened] = useState(() => pending);
  const { pathname, key } = useLocation();
  const { tutorial } = useSim();
  const first = useRef(true);
  useEffect(() => {
    const open = () => setOpened(true);
    window.addEventListener(OPEN, open);
    return () => window.removeEventListener(OPEN, open);
  }, []);
  useEffect(() => {
    // On its way to the screen under it: nothing yet.
    if (pathname === APPEARANCE_PATH) return;
    const arriving = first.current;
    first.current = false;
    if (pending) {
      pending = false;
      setOpened(true);
      return;
    }
    if (arriving) return;
    // ITS OWN ENTRY is the sheet's (`kit/backCloses.tsx`), at the same
    // address; any other move — a link out of it, a tour step, even to the
    // same screen — leaves it behind.
    const usr = (window.history.state as { usr?: { fspSheet?: unknown } } | null)?.usr;
    if (usr?.fspSheet === true) return;
    setOpened(false);
  }, [key]);
  return (
    <Sheet
      opened={opened}
      onClose={() => setOpened(false)}
      title="Appearance"
      peek
      // UNDER THE TOUR'S CARD AND ITS RING, whose chapter on the colors is
      // this sheet: the card sits at 150 on a phone and the ring at 140.
      zIndex={tutorial ? 130 : undefined}
    >
      {opened ? <AppearanceBody /> : null}
    </Sheet>
  );
}

/**
 * `/account/appearance`: back to the screen it was followed from with the
 * sheet open over it, or on to Account when there is none.
 */
export function AppearanceAddress() {
  const navigate = useNavigate();
  const { session } = useSession();
  // ONCE, even where development mounts it twice: a second step back is a
  // step out of the app.
  const went = useRef(false);
  useEffect(() => {
    if (went.current) return;
    went.current = true;
    pending = true;
    const steps = stepsToScreenBefore();
    if (steps !== null) navigate(-steps);
    else navigate(session ? '/account' : '/demo', { replace: true });
  }, []);
  return null;
}
